import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useRouterData from "@/hooks/api/useRouterData";

function normalizePath(p: string): string {
  const s = (p || "/").trim();
  if (s === "" || s === "/") return "/";
  const noTrail = s.replace(/\/+$/, "");
  return noTrail.startsWith("/") ? noTrail : `/${noTrail}`;
}

/** Treat /pre_application and /pre-application (and casing) as the same path. */
function pathSignature(p: string): string {
  try {
    const decoded = decodeURIComponent(p);
    return normalizePath(decoded).toLowerCase().replace(/_/g, "-");
  } catch {
    return normalizePath(p).toLowerCase().replace(/_/g, "-");
  }
}

export function isDynamicsComponentName(name?: string): boolean {
  if (!name || typeof name !== "string") return false;
  return name.trim().toLowerCase() === "dynamics";
}

function isFavoriteViewComponentName(name?: string): boolean {
  if (!name || typeof name !== "string") return false;
  return name.trim().toLowerCase() === "favoriteviewpage";
}

/**
 * Finds a stage-group route by pathname, with hyphen/underscore tolerant matching.
 */
export function findRouteInTabGroups(
  tabs:
    | {
        groups?: {
          routes?: {
            path?: string;
            component?: string;
            stageId?: string | number;
            viewId?: string | number;
          }[];
        }[];
      }
    | null
    | undefined,
  pathname: string
) {
  const np = pathSignature(pathname);
  if (!tabs?.groups) return null;
  for (const group of tabs.groups) {
    for (const route of group.routes || []) {
      const rp = pathSignature(route.path || "");
      if (rp === np) return route;
    }
  }
  return null;
}

/**
 * When the URL path and tab config differ slightly, NavLink still sets
 * `state.routeId` from the sidebar — use it to resolve the tab row.
 */
function findRouteByStageId(
  tabs:
    | {
        groups?: {
          routes?: {
            path?: string;
            component?: string;
            stageId?: string | number;
            viewId?: string | number;
          }[];
        }[];
      }
    | null
    | undefined,
  stageId: string | number
) {
  if (stageId === "" || stageId == null) return null;
  if (!tabs?.groups) return null;
  const sid = String(stageId);
  for (const group of tabs.groups) {
    for (const route of group.routes || []) {
      if (route.stageId != null && String(route.stageId) === sid) {
        return route;
      }
    }
  }
  return null;
}

export function useDynamicsRouteMeta() {
  const { pathname, state } = useLocation();
  const { tabs } = useRouterData();

  return useMemo(() => {
    // Share links live outside the tab config: /shared/<token> → a private
    // `shared-<token>` route that the SharedViewPage seeds from the snapshot.
    const shared = /^\/shared\/([A-Za-z0-9_-]{16,64})\/?$/.exec(pathname);
    if (shared) {
      return {
        isDynamicsRoute: false,
        isFavoriteViewRoute: false,
        isSharedViewRoute: true,
        routeId: `shared-${shared[1]}` as string | number,
        route: null,
      };
    }
    const routeFromPath = findRouteInTabGroups(tabs, pathname);
    const stateRouteId = (state as { routeId?: string | number } | null)
      ?.routeId;

    const routeFromState =
      !routeFromPath &&
      stateRouteId != null &&
      stateRouteId !== "" &&
      stateRouteId !== "default"
        ? findRouteByStageId(tabs, stateRouteId)
        : null;

    const route = routeFromPath ?? routeFromState;

    const isDynamicsRoute = isDynamicsComponentName(route?.component);
    const isFavoriteViewRoute = isFavoriteViewComponentName(route?.component);

    /**
     * A Favorite View keys its per-route state as `view-<id>`, NOT as the bare
     * id. The server also emits `stageId`/`_id` on view routes (the two sidebar
     * components read different fields for NavLink state), so a bare id would
     * make view 5 and stage 5 share one filter slice entry — the same drawer
     * state on two unrelated routes.
     */
    const viewId = (route as { viewId?: string | number } | null)?.viewId;

    // Prefer tab config stageId (matches URL); avoid stale location.state when switching stages.
    const routeId =
      viewId != null && String(viewId) !== ""
        ? `view-${viewId}`
        : route?.stageId != null && String(route.stageId) !== ""
          ? route.stageId
          : stateRouteId != null && String(stateRouteId) !== ""
            ? stateRouteId
            : "default";

    return { isDynamicsRoute, isFavoriteViewRoute, isSharedViewRoute: false, routeId, route };
  }, [tabs, pathname, state]);
}
